import React, { useState, useEffect, useRef } from 'react';
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  ChevronDown, 
  X,
  Search
} from 'lucide-react';
import styled from 'styled-components';
import { Service } from '../types';
import {
  createService,
  updateService,
  deleteService,
  getAllServices
} from '../services/api';

interface PrayerType {
  id: string;
  name: string;
  hebrewName: string;
}

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  min-width: 150px;
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px 8px 40px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  size: 16px;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const ServiceCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const ServiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ServiceName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const ServiceActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &.edit {
    background: #f3f4f6;
    color: #374151;

    &:hover {
      background: #e5e7eb;
    }
  }

  &.delete {
    background: #fef2f2;
    color: #dc2626;

    &:hover {
      background: #fee2e2;
    }
  }
`;

const ServiceDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ServiceDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const DetailLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #1f2937;
  font-weight: 600;
`;

const PrayerTypeBadge = styled.span`
  background: #dbeafe;
  color: #1e40af;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const PriceBadge = styled.span`
  background: #dcfce7;
  color: #166534;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

// Modal Components
const Modal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;

  &:hover {
    color: #374151;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'secondary' ? `
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover {
      background: #e5e7eb;
    }
  ` : `
    background: #3b82f6;
    color: white;

    &:hover {
      background: #2563eb;
    }
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyIcon = styled(Settings)`
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  color: #d1d5db;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

// Prayer types data
const PRAYER_TYPES: PrayerType[] = [
  { id: 'shacharit_early', name: 'Shacharit Early', hebrewName: 'שחרית נץ' },
  { id: 'shacharit_regular', name: 'Shacharit Regular', hebrewName: 'שחרית רגילה' },
  { id: 'mincha', name: 'Mincha', hebrewName: 'מנחה' },
  { id: 'other', name: 'Other', hebrewName: 'אחר' }
];

const Services: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [prayerTypeFilter, setPrayerTypeFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    hebrewName: '',
    prayerTypeId: ''
  });

  // Load services from database
  useEffect(() => {
    loadServices();
  }, []);

  const createDefaultServices = async () => {
    const defaultServices = [
      // שחרית נץ
      { name: 'פתיחת ההיכל', hebrewName: 'פתיחת ההיכל', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'הגבהת ספר תורה', hebrewName: 'הגבהת ספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'הולכת ספר תורה', hebrewName: 'הולכת ספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'עליה ראשונה', hebrewName: 'עליה ראשונה', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'עליה שנייה', hebrewName: 'עליה שנייה', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'עליה שלישית', hebrewName: 'עליה שלישית', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'עליה רביעית', hebrewName: 'עליה רביעית', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'עליה חמישית', hebrewName: 'עליה חמישית', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'עליה שישית', hebrewName: 'עליה שישית', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'עליה שביעית', hebrewName: 'עליה שביעית', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      { name: 'הפטרה', hebrewName: 'הפטרה', suggestedPrice: 0, prayerTypeId: 'shacharit_early' },
      // שחרית רגיל
      { name: 'פתיחת ההיכל', hebrewName: 'פתיחת ההיכל', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'הגבהת ספר תורה', hebrewName: 'הגבהת ספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'הולכת ספר תורה', hebrewName: 'הולכת ספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'עליה ראשונה', hebrewName: 'עליה ראשונה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'עליה שנייה', hebrewName: 'עליה שנייה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'עליה שלישית', hebrewName: 'עליה שלישית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'עליה רביעית', hebrewName: 'עליה רביעית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'עליה חמישית', hebrewName: 'עליה חמישית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'עליה שישית', hebrewName: 'עליה שישית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'עליה שביעית', hebrewName: 'עליה שביעית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      { name: 'הפטרה', hebrewName: 'הפטרה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular' },
      // מנחה
      { name: 'פתיחת ההיכל', hebrewName: 'פתיחת ההיכל', suggestedPrice: 0, prayerTypeId: 'mincha' },
      { name: 'הגבהת ספר תורה', hebrewName: 'הגבהת ספר תורה', suggestedPrice: 0, prayerTypeId: 'mincha' },
      { name: 'הולכת ספר תורה', hebrewName: 'הולכת ספר תורה', suggestedPrice: 0, prayerTypeId: 'mincha' },
      { name: 'עליה ראשונה', hebrewName: 'עליה ראשונה', suggestedPrice: 0, prayerTypeId: 'mincha' },
      { name: 'עליה שנייה', hebrewName: 'עליה שנייה', suggestedPrice: 0, prayerTypeId: 'mincha' },
      { name: 'עליה שלישית', hebrewName: 'עליה שלישית', suggestedPrice: 0, prayerTypeId: 'mincha' },
      // אחר
      { name: 'שירות כללי', hebrewName: 'שירות כללי', suggestedPrice: 0, prayerTypeId: 'other' }
    ];

    for (const service of defaultServices) {
      await createService(service);
    }
  };

  const loadServices = async () => {
    try {
      const servicesData = await getAllServices();
      if (servicesData.length === 0) {
        // If no services exist, create the default ones
        await createDefaultServices();
        const newServicesData = await getAllServices();
        setServices(newServicesData);
      } else {
        setServices(servicesData);
      }
    } catch (error) {
      console.error('Error loading services:', error);
      // Fallback to mock data if database is empty
      const mockServices: Service[] = [
      // שחרית נץ
      {
        id: '1',
        name: 'פתיחת ההיכל',
        hebrewName: 'פתיחת ההיכל',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '2',
        name: 'הגבהת ספר תורה',
        hebrewName: 'הגבהת ספר תורה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '3',
        name: 'הולכת ספר תורה',
        hebrewName: 'הולכת ספר תורה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '4',
        name: 'עליה ראשונה',
        hebrewName: 'עליה ראשונה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '5',
        name: 'עליה שנייה',
        hebrewName: 'עליה שנייה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '6',
        name: 'עליה שלישית',
        hebrewName: 'עליה שלישית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '7',
        name: 'עליה רביעית',
        hebrewName: 'עליה רביעית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '8',
        name: 'עליה חמישית',
        hebrewName: 'עליה חמישית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '9',
        name: 'עליה שישית',
        hebrewName: 'עליה שישית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '10',
        name: 'עליה שביעית',
        hebrewName: 'עליה שביעית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      {
        id: '11',
        name: 'הפטרה',
        hebrewName: 'הפטרה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_early',
        createdAt: new Date()
      },
      // שחרית רגיל
      {
        id: '12',
        name: 'פתיחת ההיכל',
        hebrewName: 'פתיחת ההיכל',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '13',
        name: 'הגבהת ספר תורה',
        hebrewName: 'הגבהת ספר תורה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '14',
        name: 'הולכת ספר תורה',
        hebrewName: 'הולכת ספר תורה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '15',
        name: 'עליה ראשונה',
        hebrewName: 'עליה ראשונה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '16',
        name: 'עליה שנייה',
        hebrewName: 'עליה שנייה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '17',
        name: 'עליה שלישית',
        hebrewName: 'עליה שלישית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '18',
        name: 'עליה רביעית',
        hebrewName: 'עליה רביעית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '19',
        name: 'עליה חמישית',
        hebrewName: 'עליה חמישית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '20',
        name: 'עליה שישית',
        hebrewName: 'עליה שישית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '21',
        name: 'עליה שביעית',
        hebrewName: 'עליה שביעית',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      {
        id: '22',
        name: 'הפטרה',
        hebrewName: 'הפטרה',
        suggestedPrice: 0,
        prayerTypeId: 'shacharit_regular',
        createdAt: new Date()
      },
      // מנחה
      {
        id: '23',
        name: 'פתיחת ההיכל',
        hebrewName: 'פתיחת ההיכל',
        suggestedPrice: 0,
        prayerTypeId: 'mincha',
        createdAt: new Date()
      },
      {
        id: '24',
        name: 'הגבהת ספר תורה',
        hebrewName: 'הגבהת ספר תורה',
        suggestedPrice: 0,
        prayerTypeId: 'mincha',
        createdAt: new Date()
      },
      {
        id: '25',
        name: 'הולכת ספר תורה',
        hebrewName: 'הולכת ספר תורה',
        suggestedPrice: 0,
        prayerTypeId: 'mincha',
        createdAt: new Date()
      },
      {
        id: '26',
        name: 'עליה ראשונה',
        hebrewName: 'עליה ראשונה',
        suggestedPrice: 0,
        prayerTypeId: 'mincha',
        createdAt: new Date()
      },
      {
        id: '27',
        name: 'עליה שנייה',
        hebrewName: 'עליה שנייה',
        suggestedPrice: 0,
        prayerTypeId: 'mincha',
        createdAt: new Date()
      },
      {
        id: '28',
        name: 'עליה שלישית',
        hebrewName: 'עליה שלישית',
        suggestedPrice: 0,
        prayerTypeId: 'mincha',
        createdAt: new Date()
      },
      // אחר
      {
        id: '29',
        name: 'שירות כללי',
        hebrewName: 'שירות כללי',
        suggestedPrice: 0,
        prayerTypeId: 'other',
        createdAt: new Date()
      }
    ];
    setServices(mockServices);
    }
  };

  // Filter services
  useEffect(() => {
    let filtered = services;

    if (searchTerm) {
      filtered = filtered.filter(service =>
        service.hebrewName.includes(searchTerm)
      );
    }

    if (prayerTypeFilter !== 'all') {
      filtered = filtered.filter(service => service.prayerTypeId === prayerTypeFilter);
    }

    // Sort by Hebrew name alphabetically
    filtered = filtered.sort((a, b) => a.hebrewName.localeCompare(b.hebrewName, 'he'));

    setFilteredServices(filtered);
  }, [services, searchTerm, prayerTypeFilter]);

  const handleAddService = () => {
    setEditingService(null);
    setFormData({
      hebrewName: '',
      prayerTypeId: ''
    });
    setIsModalOpen(true);
  };

  const handleEditService = (service: Service) => {
    setEditingService(service);
    setFormData({
      hebrewName: service.hebrewName,
      prayerTypeId: service.prayerTypeId
    });
    setIsModalOpen(true);
  };

  const handleSaveService = async () => {
    if (!formData.hebrewName || !formData.prayerTypeId) {
      alert('אנא מלא את כל השדות הנדרשים');
      return;
    }

    try {
      const serviceData = {
        name: formData.hebrewName,
        hebrewName: formData.hebrewName,
        suggestedPrice: 0,
        prayerTypeId: formData.prayerTypeId
      };

      if (editingService) {
        await updateService(editingService.id, serviceData);
      } else {
        await createService(serviceData);
      }

      await loadServices(); // Reload services from database
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving service:', error);
      alert('שגיאה בשמירת השירות');
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את השירות?')) {
      try {
        await deleteService(serviceId);
        await loadServices(); // Reload services from database
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('שגיאה במחיקת השירות');
      }
    }
  };

  const getPrayerTypeName = (prayerTypeId: string) => {
    const prayerType = PRAYER_TYPES.find(p => p.id === prayerTypeId);
    return prayerType?.hebrewName || prayerTypeId;
  };

  return (
    <Container>
      <Header>
        <Title>
          <Settings size={32} />
          ניהול שירותים
        </Title>
        <AddButton onClick={handleAddService}>
          <Plus size={20} />
          הוסף שירות
        </AddButton>
      </Header>

      <FilterBar>
        <SearchBar>
          <SearchInput
            type="text"
            placeholder="חיפוש שירות..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon size={16} />
        </SearchBar>
        <FilterSelect
          value={prayerTypeFilter}
          onChange={(e) => setPrayerTypeFilter(e.target.value)}
        >
          <option value="all">כל סוגי התפילות</option>
          {PRAYER_TYPES.map(prayerType => (
            <option key={prayerType.id} value={prayerType.id}>
              {prayerType.hebrewName}
            </option>
          ))}
        </FilterSelect>
      </FilterBar>

      {filteredServices.length === 0 ? (
        <EmptyState>
          <EmptyIcon />
          <EmptyTitle>אין שירותים</EmptyTitle>
          <EmptyDescription>
            {searchTerm || prayerTypeFilter !== 'all' 
              ? 'לא נמצאו שירותים התואמים לחיפוש'
              : 'הוסף שירות ראשון כדי להתחיל'
            }
          </EmptyDescription>
        </EmptyState>
      ) : (
        <ServicesGrid>
          {filteredServices.map(service => (
            <ServiceCard key={service.id}>
              <ServiceHeader>
                <ServiceName>{service.hebrewName}</ServiceName>
                <ServiceActions>
                  <ActionButton
                    className="edit"
                    onClick={() => handleEditService(service)}
                  >
                    <Edit size={16} />
                  </ActionButton>
                  <ActionButton
                    className="delete"
                    onClick={() => handleDeleteService(service.id)}
                  >
                    <Trash2 size={16} />
                  </ActionButton>
                </ServiceActions>
              </ServiceHeader>
              <ServiceDetails>
                <ServiceDetail>
                  <DetailLabel>סוג תפילה:</DetailLabel>
                  <PrayerTypeBadge>{getPrayerTypeName(service.prayerTypeId)}</PrayerTypeBadge>
                </ServiceDetail>
              </ServiceDetails>
            </ServiceCard>
          ))}
        </ServicesGrid>
      )}

      {/* Add/Edit Service Modal */}
      <Modal $isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {editingService ? 'ערוך שירות' : 'הוסף שירות חדש'}
            </ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>שם השירות *</Label>
            <Input
              type="text"
              value={formData.hebrewName}
              onChange={(e) => setFormData(prev => ({ ...prev, hebrewName: e.target.value }))}
              placeholder="שם השירות"
            />
          </FormGroup>

          <FormGroup>
            <Label>סוג תפילה *</Label>
            <Select
              value={formData.prayerTypeId}
              onChange={(e) => setFormData(prev => ({ ...prev, prayerTypeId: e.target.value }))}
            >
              <option value="">בחר סוג תפילה</option>
              {PRAYER_TYPES.map(prayerType => (
                <option key={prayerType.id} value={prayerType.id}>
                  {prayerType.hebrewName}
                </option>
              ))}
            </Select>
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              ביטול
            </Button>
            <Button onClick={handleSaveService}>
              {editingService ? 'עדכן' : 'הוסף'}
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default Services;
